import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }

.asst-wrap { max-width: 820px; margin: 0 auto; padding: 20px 20px 0; display: flex; flex-direction: column; height: calc(100vh - 105px); }
.asst-scroll { flex: 1; overflow-y: auto; padding-bottom: 16px; }
.asst-intro { text-align: center; padding: 32px 0 20px; }
.asst-intro h1 { font-size: 19px; font-weight: 700; color: var(--text); }
.asst-intro p { font-size: 13px; color: var(--muted); margin-top: 6px; line-height: 1.5; }
.asst-chips { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 18px; }
.asst-chip { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; padding: 7px 14px; font-size: 12.5px; color: var(--text2); cursor: pointer; font-family: inherit; }
.asst-chip:hover { border-color: var(--accent); color: var(--accent); }

.asst-msg { display: flex; margin: 14px 0; }
.asst-msg.user { justify-content: flex-end; }
.asst-bubble { max-width: 80%; padding: 11px 15px; border-radius: 14px; font-size: 13.5px; line-height: 1.6; white-space: pre-wrap; word-wrap: break-word; }
.asst-msg.user .asst-bubble { background: var(--accent); color: #fff; border-bottom-right-radius: 4px; }
.asst-msg.assistant .asst-bubble { background: var(--surface); border: 1px solid var(--border); color: var(--text); border-bottom-left-radius: 4px; }
.asst-bubble table { border-collapse: collapse; margin: 8px 0; font-size: 12.5px; }
.asst-bubble th, .asst-bubble td { border: 1px solid var(--border2); padding: 4px 8px; text-align: left; }

.asst-typing { display: inline-flex; gap: 4px; padding: 4px 0; }
.asst-typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--muted); animation: bounce 1.2s infinite; }
.asst-typing span:nth-child(2) { animation-delay: .15s; }
.asst-typing span:nth-child(3) { animation-delay: .3s; }
@keyframes bounce { 0%, 60%, 100% { opacity: .3; } 30% { opacity: 1; } }

.asst-err { background: rgba(220,38,38,.08); border: 1px solid rgba(220,38,38,.25); color: #dc2626; font-size: 12.5px; padding: 9px 13px; border-radius: 9px; margin: 10px 0; }

.asst-inputbar { border-top: 1px solid var(--border2); padding: 14px 0 16px; display: flex; gap: 10px; align-items: flex-end; background: var(--bg); }
.asst-input { flex: 1; resize: none; background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 11px 14px; font-size: 13.5px; color: var(--text); outline: none; font-family: inherit; max-height: 140px; line-height: 1.5; }
.asst-input:focus { border-color: var(--accent); }
.asst-send { background: var(--accent); color: #fff; border: none; border-radius: 12px; padding: 11px 18px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; flex-shrink: 0; }
.asst-send:disabled { opacity: .45; cursor: default; }
.asst-disclaimer { text-align: center; font-size: 10.5px; color: var(--muted); padding-bottom: 10px; }
`;

// Minimal, safe markdown -> HTML for the assistant's replies: bold, inline
// code, bullet lists, and pipe tables. Everything is HTML-escaped first, so
// no user/model text can inject markup.
function renderMarkdown(src) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = src.split("\n");
  let html = "";
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { rows.push(lines[i]); i++; }
      const cells = (r) => r.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(rows[0]);
      const body = rows.slice(2).map(cells);
      html += "<table><thead><tr>" + head.map((h) => `<th>${inline(h)}</th>`).join("") + "</tr></thead><tbody>" +
        body.map((r) => "<tr>" + r.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>").join("") + "</tbody></table>";
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      html += "<ul>";
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        html += `<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>`;
        i++;
      }
      html += "</ul>";
      continue;
    }
    if (line.trim() === "") { html += "<br/>"; i++; continue; }
    html += `<div>${inline(line)}</div>`;
    i++;
  }
  function inline(s) {
    return esc(s)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
  }
  return html;
}

export default function HrmsAssistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const [messages, setMessages] = useState([]); // {role, content, steps?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [examples, setExamples] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/hr-assistant/examples").then((r) => setExamples(r.data.examples || [])).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setError("");
    setInput("");
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setLoading(true);
    try {
      const res = await api.post("/hr-assistant/chat", {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...next, { role: "assistant", content: res.data.reply, steps: res.data.steps || [] }]);
    } catch (e) {
      setError(e.response?.data?.detail || "The assistant could not answer that. Try rephrasing.");
      setMessages(next);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")}>&#8592;</button>
        <div className="adm-brand">
          <div className="adm-brand-mark">💬</div>
          <span className="adm-brand-label">HRMS & Payroll — AI Assistant</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab" onClick={() => navigate("/hrms/dashboard")}>Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/payroll")}>Payroll</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/expiry")}>Document Expiry</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/data-quality")}>Data Quality</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/company-documents")}>Company Documents</button>
        <button className="adm-tab active">AI Assistant</button>
      </div>

      <div className="asst-wrap">
        <div className="asst-scroll" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="asst-intro">
              <h1>Ask about your employees and payroll</h1>
              <p>I can look things up, but not change anything.</p>
              <div className="asst-chips">
                {examples.map((ex, i) => (
                  <button key={i} className="asst-chip" onClick={() => send(ex)}>{ex}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`asst-msg ${m.role}`}>
              <div className="asst-bubble">
                {m.role === "assistant"
                  ? <span dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content || "") }} />
                  : m.content}
              </div>
            </div>
          ))}

          {loading && (
            <div className="asst-msg assistant">
              <div className="asst-bubble">
                <span className="asst-typing"><span /><span /><span /></span>
              </div>
            </div>
          )}

          {error && <div className="asst-err">{error}</div>}
        </div>

        <div className="asst-inputbar">
          <textarea
            ref={inputRef}
            className="asst-input"
            rows={1}
            placeholder="Ask a question about your employees or payroll…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
          />
          <button className="asst-send" onClick={() => send()} disabled={loading || !input.trim()}>Send</button>
        </div>
        <div className="asst-disclaimer">
          The assistant reads data only. Always confirm important figures against the record itself.
        </div>
      </div>
    </>
  );
}
